import { ELKLayoutConfig } from './elk-types'

/**
 * Default ELK layout configuration for journey workflows
 * 
 * This configuration provides:
 * - Layered algorithm optimized for directed acyclic graphs (DAGs)
 * - Top-to-bottom layout direction
 * - Orthogonal (Manhattan-style) edge routing with 90-degree angles
 * - Consistent spacing between nodes and layers
 * - Adequate padding around the workflow content
 */
export const DEFAULT_ELK_CONFIG: ELKLayoutConfig = {
    algorithm: 'layered',            // Layered algorithm for DAGs
    direction: 'DOWN',               // Top-to-bottom layout
    edgeRouting: 'ORTHOGONAL',       // Manhattan-style routing with 90-degree angles
    nodeSeparation: 80,              // Horizontal space between nodes (px)
    rankSeparation: 90,              // Vertical space between levels (px)
    padding: 40,                     // Padding around the graph (px)
}

/**
 * Layout constants for journey builder
 * 
 * These constants control various aspects of the layout system:
 * - Node dimensions and constraints
 * - Viewport sizing and constraints
 * 
 * Note: MIN_NODE_WIDTH should match the CSS min-width of .custom-node (250px)
 * to prevent layout flickering when nodes are measured by React Flow
 */
export const LAYOUT_CONSTANTS = {
    MIN_NODE_HEIGHT: 60,             // Minimum node height (px)
    MIN_NODE_WIDTH: 250,             // Minimum node width (px) - matches CSS min-width
    MIN_VIEWPORT_WIDTH: 800,         // Minimum viewport width (px)
    MIN_VIEWPORT_HEIGHT: 600,        // Minimum viewport height (px)
} as const

/**
 * Layout constants for split step variant edge rendering
 *
 * These control spacing of variant lanes and the orthogonal edge geometry
 * (bends, convergence level, corner radius) when ELK edge routing is used.
 */
export const SPLIT_LAYOUT_CONSTANTS = {
    LANE_OFFSET: 350,           // Horizontal distance between variant lane centers
    BEND_GAP: 40,               // Vertical distance from port to the horizontal bend
    CONVERGE_GAP: 20,           // Vertical distance from join target top to convergence level
    CORNER_RADIUS: 12,          // Radius for rounded corners on orthogonal edge bends
    MIN_LANE_GAP: 40,           // Minimum inter-lane content gap (px)
    ALIGN_TOLERANCE: 8,         // |dx| under which an edge is treated as vertically aligned → routed straight down (px)
    EMPTY_LANE_GAP_FACTOR: 0.6, // Compact gap factor for the two-empty-variant case (× splitWidth)
    PORT_X_CLAMP_MIN: 0.1,      // Lower bound for clamping port X within the split node (fraction of splitWidth)
    PORT_X_CLAMP_MAX: 0.9,      // Upper bound for clamping port X within the split node (fraction of splitWidth)
} as const
